import React, { FC } from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';
// constants
import routes from '../../../constants/routes';

// helpers
import { formatNumberFull } from '../../../helpers/numeric';

// models
import { FormulaCategory } from '../../../models/formula.model';

import MessagesProgressBar from './MessagesProgressBar';

interface ChannelInfo {
    channel: string;
    percentage: number;
    color: string;
}
interface DraftMessagesProps {
    heading: string;
    tableData: any;
    calculatePercentages: () => ChannelInfo[];
    totalMessages: number;
}

const DraftMessagesMain: FC<React.PropsWithChildren<DraftMessagesProps>> = ({
    heading,
    tableData,
    totalMessages,
    calculatePercentages,
}) => {
    const getRowUrl = (messageType: string) =>
        routes.formulasCatalogWithTabs(FormulaCategory.Draft) +
        `?message_type=${messageType}`;

    return (
        <Container>
            <HeaderContainer>
                <Heading>{heading}</Heading>
                <ViewMessagesLink
                    to={routes.formulasCatalogWithTabs(FormulaCategory.Draft)}
                >
                    View messages
                </ViewMessagesLink>
            </HeaderContainer>
            {totalMessages > 0 && (
                <MessagesProgressBar channels={calculatePercentages()} />
            )}

            <>
                <Table>
                    <thead>
                        <HeaderRow>
                            <FirstHeaderCell>Channel</FirstHeaderCell>
                            <HeaderCell>Messages</HeaderCell>
                            <LabelsHeaderCell>Alternates</LabelsHeaderCell>
                            <LabelsHeaderCell>Last updated</LabelsHeaderCell>
                        </HeaderRow>
                    </thead>
                    <TableBody>
                        {tableData &&
                            tableData()?.map((rowData: any, index: number) => (
                                <TableRowLink
                                    key={index}
                                    to={getRowUrl(rowData?.messageType)}
                                >
                                    <FirstTableCell>
                                        {rowData?.channel}
                                    </FirstTableCell>
                                    <MessagesTableCell>
                                        {formatNumberFull(rowData?.messages)}
                                    </MessagesTableCell>
                                    <MessagesTableCell>
                                        {formatNumberFull(
                                            rowData?.uniqueMessages,
                                        )}
                                    </MessagesTableCell>
                                    <TableCell>
                                        {rowData?.lastUpdated}
                                    </TableCell>
                                </TableRowLink>
                            ))}
                    </TableBody>
                </Table>
            </>
        </Container>
    );
};

export default DraftMessagesMain;

const Container = styled.div({
    display: 'flex',
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing(3),
    width: '100%',
    borderRadius: aampeTheme.borderRadius.default,
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
    marginBottom: aampeTheme.spacing(2),
});

const HeaderContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
});

const Heading = styled.span({
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '28px',
    color: aampeTheme.colors.textAccessibleWG01,
});

const Table = styled.table({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
});

const HeaderRow = styled.tr({
    display: 'table',
    width: '100%',
    tableLayout: 'fixed',
    color: aampeTheme.colors.neutral09,
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 500,
});
const FirstHeaderCell = styled.th({
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    paddingLeft: aampeTheme.spacing(2),
    width: '22%',
});
const LabelsHeaderCell = styled.th({
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    width: '15%',
});
const HeaderCell = styled.th({
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    width: '15%',
});

const TableRowLink = styled(Link)({
    display: 'flex',
    width: '100%',
    tableLayout: 'fixed',
    fontSize: '16px',
    fontWeight: 400,
    borderRadius: aampeTheme.borderRadius.default,
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
        color: aampeTheme.colors.solidA01,
    },
});

const TableBody = styled.tbody({
    display: 'flex',
    flexDirection: 'column',
});
const TableCell = styled.div({
    flex: 1,
    padding: `${aampeTheme.spacing(0, 0, 3, 2)}`,
    alignItems: 'center',
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    color: 'inherit',
});
const MessagesTableCell = styled(TableCell)({});
const FirstTableCell = styled.div({
    padding: `${aampeTheme.spacing(0, 0, 3, 0)}`,
    flex: 1.5,
    alignItems: 'center',
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'left',
    color: 'inherit',
});
const ViewMessagesLink = styled(Link)({
    color: aampeTheme.colors.solidA01,
    border: 'none',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '18px',
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
});
